import truncate from 'lodash/truncate';

import { PRIMARY_FILTER_BY_AI_ENTITY } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import { withLegacyTableColumns } from '../CustomDash/utils/table-columns-serialization';
import type { GlobalAssistantEntityType } from './global-ai-assistant-api';
import type { GlobalAiConversationSummary } from './global-ai-assistant-conversation';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import type { ReasoningTrace } from './reasoning-trace';

/**
 * Version stamp for the persisted conversation envelope. Bump when a
 * transcript change is NOT tolerable by the unknown-kind skip below (e.g.
 * a renamed field on an existing message kind), and add a migration in
 * `parseConversationPayload`.
 */
export const GLOBAL_AI_CONVERSATION_SCHEMA_VERSION = 1;

export type GlobalAiConversationPayloadV1 = {
  schemaVersion: 1;
  transcript: GlobalAiTranscriptMessage[];
  conversationSummary: GlobalAiConversationSummary | null;
  sessionEntityScope: GlobalAssistantEntityType | null;
};

/** Metadata row returned by the list endpoint. */
export type GlobalAiConversationListItem = {
  id: string;
  title: string;
  updatedAt: string;
};

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/**
 * True when an assistant message carries the fields its kind's serialize /
 * rehydrate / render paths dereference WITHOUT optional chaining. A
 * kind-only check would let a corrupt or cross-version message (e.g. a
 * `reasoning_trace` with no `trace`, a `single_chart` with no `item`)
 * survive the load gate and then throw in the map below or in the renderer.
 */
function assistantMessageHasRequiredShape(
  kind: string,
  m: Record<string, unknown>
): boolean {
  switch (kind) {
    // No payload beyond id/role/kind — the caller already validated both.
    case 'stopped':
      return true;
    case 'text':
    case 'clarification':
    case 'error':
      return typeof m.text === 'string';
    case 'single_chart':
      return isObj(m.item) && typeof m.entityType === 'string';
    case 'report_offers':
      return Array.isArray(m.offers);
    case 'nav_confirm':
      return typeof m.optionId === 'string' && typeof m.pageName === 'string';
    case 'create_page_confirm':
      return typeof m.prompt === 'string' && typeof m.entityType === 'string';
    case 'reasoning_trace':
      return (
        isObj(m.trace) &&
        Array.isArray((m.trace as { steps?: unknown }).steps) &&
        // Without a numeric startedAt the header math renders "Worked for
        // NaNs" — reject the message rather than pass corrupt time data.
        typeof (m.trace as { startedAt?: unknown }).startedAt === 'number'
      );
    default:
      return false;
  }
}

/**
 * Force a trace into a terminal state for rehydration: stamp `endedAt` and
 * coerce any step still `running` to `done`, so a trace persisted from a
 * turn cut short (engine unmount / mid-turn abort) never renders a header
 * "Worked for Ns" alongside a forever-spinning step row.
 */
function finalizeTraceForRehydrate(
  trace: ReasoningTrace,
  nowMs: number
): ReasoningTrace {
  return {
    ...trace,
    endedAt: trace.endedAt ?? nowMs,
    steps: trace.steps.map((s) =>
      s.status === 'running' ? { ...s, status: 'done' } : s
    ),
  };
}

function traceNeedsFinalize(trace: ReasoningTrace): boolean {
  return (
    trace.endedAt === undefined ||
    trace.steps.some((s) => s.status === 'running')
  );
}

/**
 * Save-time normalization:
 * - drop `ephemeral` progress narration (on-screen chatter, never history)
 * - force `endedAt` on reasoning traces so a rehydrated trace can never
 *   render an eternal "Working…" block
 * - give each chart the `tableColumns` its legacy plot fields describe
 * Filter artifacts and confirm-chip `resolved` flags persist as-is.
 */
/** Force every trace in the transcript into a rehydratable terminal state. */
function finalizeTranscriptTraces(
  transcript: GlobalAiTranscriptMessage[],
  nowMs: number
): GlobalAiTranscriptMessage[] {
  return transcript.map((m) =>
    m.role === 'assistant' &&
    m.kind === 'reasoning_trace' &&
    traceNeedsFinalize(m.trace)
      ? { ...m, trace: finalizeTraceForRehydrate(m.trace, nowMs) }
      : m
  );
}

const withChartTableColumns = (
  message: GlobalAiTranscriptMessage
): GlobalAiTranscriptMessage =>
  message.role === 'assistant' && message.kind === 'single_chart'
    ? {
        ...message,
        item: withLegacyTableColumns(
          message.item,
          PRIMARY_FILTER_BY_AI_ENTITY[message.entityType]
        ),
      }
    : message;

export function serializeConversation(args: {
  transcript: GlobalAiTranscriptMessage[];
  conversationSummary: GlobalAiConversationSummary | null;
  sessionEntityScope: GlobalAssistantEntityType | null;
  nowMs: number;
}): GlobalAiConversationPayloadV1 {
  const transcript = finalizeTranscriptTraces(
    args.transcript
      .filter(
        (m) => !(m.role === 'assistant' && m.kind === 'text' && m.ephemeral)
      )
      .map(withChartTableColumns),
    args.nowMs
  );
  return {
    schemaVersion: 1,
    transcript,
    conversationSummary: args.conversationSummary,
    sessionEntityScope: args.sessionEntityScope,
  };
}

export type ParsedConversationPayload =
  | { ok: true; payload: GlobalAiConversationPayloadV1 }
  | { ok: false; reason: 'newer_schema' | 'malformed' };

function isKnownTranscriptMessage(
  value: unknown
): value is GlobalAiTranscriptMessage {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const m = value as Record<string, unknown>;
  if (typeof m.id !== 'string') {
    return false;
  }
  if (m.role === 'user') {
    return typeof m.text === 'string';
  }
  if (m.role !== 'assistant') {
    return false;
  }
  return (
    typeof m.kind === 'string' && assistantMessageHasRequiredShape(m.kind, m)
  );
}

/**
 * Load-time guards: reject payloads from a NEWER client (surface a refresh
 * prompt rather than a corrupt render), skip message kinds this client
 * doesn't know (forward compatibility as the union evolves), and re-force
 * trace `endedAt` in case an older writer missed it.
 */
export function parseConversationPayload(
  raw: unknown,
  nowMs: number
): ParsedConversationPayload {
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, reason: 'malformed' };
  }
  const candidate = raw as {
    schemaVersion?: unknown;
    transcript?: unknown;
    conversationSummary?: unknown;
    sessionEntityScope?: unknown;
  };
  if (
    typeof candidate.schemaVersion !== 'number' ||
    !Array.isArray(candidate.transcript)
  ) {
    return { ok: false, reason: 'malformed' };
  }
  if (candidate.schemaVersion > GLOBAL_AI_CONVERSATION_SCHEMA_VERSION) {
    return { ok: false, reason: 'newer_schema' };
  }
  const transcript = finalizeTranscriptTraces(
    candidate.transcript.filter(isKnownTranscriptMessage),
    nowMs
  );
  // Malformed summaries degrade to null (a missing summary just means the
  // next turn re-sends older turns verbatim) rather than passing garbage
  // through to prompt construction.
  const rawSummary = candidate.conversationSummary;
  const conversationSummary =
    typeof rawSummary === 'object' &&
    rawSummary !== null &&
    typeof (rawSummary as { text?: unknown }).text === 'string' &&
    typeof (rawSummary as { upToTurnIndex?: unknown }).upToTurnIndex ===
      'number'
      ? (rawSummary as GlobalAiConversationSummary)
      : null;
  // Only a string entity-scope is meaningful downstream (it flows into prompt
  // construction and path inference); anything else degrades to null.
  const rawScope = candidate.sessionEntityScope;
  const sessionEntityScope =
    typeof rawScope === 'string'
      ? (rawScope as GlobalAssistantEntityType)
      : null;
  return {
    ok: true,
    payload: {
      schemaVersion: 1,
      transcript,
      conversationSummary,
      sessionEntityScope,
    },
  };
}

/** Fallback title when LLM title generation fails or hasn't landed yet. */
export function fallbackConversationTitle(
  transcript: GlobalAiTranscriptMessage[]
): string {
  const firstUser = transcript.find((m) => m.role === 'user');
  const text = firstUser && 'text' in firstUser ? firstUser.text.trim() : '';
  if (!text) {
    return 'New chat';
  }
  return truncate(text, { length: 60 });
}
